import React from 'react'
import _ from 'lodash'
import {Button, IconButton, S1, S2, P, Row, Popup, Panel, ListItem, H5} from '../../UI/index.js'
import CarrierSelector from '../../parcels/CarrierSelector.js'
import {formSpacing, FormStack, Input, CurrencyInput, Select} from '../../UI/forms/index.js'
import PriceTableModal from './PriceTableModal.js'
import ImportPriceTableModal from './ImportPriceTableModal.js'
import ParcelFeeModal from './ParcelFeeModal.js'
import type {ParcelPriceTable, ParcelFee, Reseller} from '../../../../types/types.js'
import {ParcelCarrier} from 'server/functions/parcels/carrier.types.js'
import naturalSort from 'src/utils/naturalSort.js'

interface PriceTableProps {
    carriers: ParcelCarrier[]
    reseller: Reseller
    onChange: (reseller: Reseller, path: string | string[], removedPath?: string | string[]) => void
    onUpdateCustomers: (newName: string, oldName: string, callback: (err?: Error) => void) => void
}

interface PriceTableState {
    selectedCarrier: ParcelCarrier
    selectedPriceTable: string
    copiedPriceTable?: any
}

export default class PriceTable extends React.Component<PriceTableProps, PriceTableState> {
    PriceTableModal: any
    importPriceTableModal: any
    popup: any
    parcelFeeModal: any

    constructor(props: PriceTableProps) {
        super(props)

        this.state = {
            selectedCarrier: props.carriers[0] || {} as ParcelCarrier,
            selectedPriceTable: 'Standaard'
        }

        addEventListener('paste', (event: ClipboardEvent) => {
            let pastedText: any

            try {
                pastedText = JSON.parse(event.clipboardData?.getData('text') || '')
            } catch {
                //
            }

            if (pastedText?.name && pastedText?.priceTable) {
                this.onClickAddPriceTable(pastedText.name, pastedText.priceTable)
            }
        })
    }

    onChangePriceTable(event: React.ChangeEvent<HTMLSelectElement>) {
        this.setState({selectedPriceTable: event.target.value})
    }

    onChangeCarrier(event: React.ChangeEvent<HTMLSelectElement>) {
        const {carriers} = this.props

        const selectedCarrier = carriers.find((c) => c.name === event.target.value)

        if (selectedCarrier) {
            this.setState({selectedCarrier})
        }
    }

    onClickAddPriceTable(name: string, priceTable: ParcelPriceTable) {
        const {reseller, onChange} = this.props

        if (!reseller.settings.parcels.prices[name]) {
            reseller.settings.parcels.prices = reseller.settings.parcels.prices || {}
            reseller.settings.parcels.prices[name] = priceTable
            this.setState({selectedPriceTable: name})
            onChange(reseller, ['parcels', 'prices', name])
        }
    }

    async onClickOpenPriceTableModal(name: string, oldName?: string, priceTable?: ParcelPriceTable) {
        this.PriceTableModal.open(name, oldName, structuredClone(priceTable))

        if (priceTable) {
            await navigator.clipboard.writeText(JSON.stringify({name, priceTable}))
        }
    }

    onEditPriceTable(name: string, oldName: string, priceTable: ParcelPriceTable) {
        const {reseller, onChange, onUpdateCustomers} = this.props

        onUpdateCustomers(name, oldName, (err) => {
            if (!err) {
                delete reseller.settings.parcels.prices[oldName]
                reseller.settings.parcels.prices = reseller.settings.parcels.prices || {}
                reseller.settings.parcels.prices[name] = priceTable

                this.setState({selectedPriceTable: name})
                if (oldName !== name) {
                    onChange(reseller, ['parcels', 'prices', name], ['parcels', 'prices', oldName])
                } else {
                    onChange(reseller, ['parcels', 'prices', name])
                }
            }
        })
    }

    onRemovePriceTable(name: string) {
        const {reseller, onChange, onUpdateCustomers} = this.props

        onUpdateCustomers('', name, (err) => {
            if (!err) {
                delete reseller.settings.parcels.prices[name]
                this.setState({selectedPriceTable: 'Standaard'})
                onChange(reseller, 'parcels.prices', ['parcels', 'prices', name])
            }
        })
    }

    onClickCopyCarrierPriceTable(priceTable?: ParcelPriceTable, carrier?: ParcelCarrier) {
        this.setState({copiedPriceTable: priceTable?.[carrier?.name || ''] || {}})
    }

    onSaveFee(fee: ParcelFee, index: number | null) {
        const {reseller, onChange} = this.props

        if (index !== null && index >= 0) {
            reseller.settings.parcels.fees[index] = fee
        } else {
            reseller.settings.parcels.fees.push(fee)
        }

        onChange(reseller, 'parcels.fees')
    }

    onRemoveFee(index: number) {
        const {reseller, onChange} = this.props

        reseller.settings.parcels.fees.splice(index, 1)

        onChange(reseller, 'parcels.fees')
    }

    onClickExportTable() {
        const {reseller} = this.props

        const exportObj = {
            resellerName: reseller.name,
            priceTables: reseller.settings.parcels.prices || {},
            fees: reseller.settings.parcels.fees || []
        }

        const blob = new Blob([JSON.stringify(exportObj)], {type: 'application/json'})

        const linkEl = document.createElement('a')
        linkEl.href = URL.createObjectURL(blob)
        linkEl.setAttribute('download', `${exportObj.resellerName} Tarieven`)
        linkEl.click()
    }

    onClickImportTable() {
        const {onChange} = this.props

        this.importPriceTableModal?.open((reseller: Reseller) => {
            Object.keys(reseller.settings.parcels.prices).forEach((priceTable) => {
                onChange(reseller, ['parcels', 'prices', priceTable])
            })
            onChange(reseller, 'parcels.fees')
        })
    }

    render() {
        const {selectedPriceTable, selectedCarrier, copiedPriceTable} = this.state
        const {reseller, carriers} = this.props

        const enabledCarriers = _.filter(carriers, (carrier) => {
            return reseller.settings.parcels.carriers[carrier.name] && reseller.settings.parcels.carriers[carrier.name].enabled
        })

        const priceTables = _.uniq(['Standaard', ...naturalSort<string>(_.keys(reseller.settings.parcels.prices))])
        const priceTable = reseller.settings.parcels.prices[selectedPriceTable] || {}

        let selectedCarrierProducts = []

        if (selectedCarrier && selectedCarrier.products) {
            selectedCarrierProducts = [
                'Standaard zending',
                ...naturalSort<string>(_.keys(selectedCarrier.products))
            ]
        }

        return (
            <>
                <div>
                    <Row style={{marginBottom: 24}} />

                    <div style={{display: 'flex', alignItems: 'center', columnGap: formSpacing.columnGap, marginBottom: 36}}>
                        <Select
                            style={{width: 300}}
                            label='Tarieftabel'
                            value={selectedPriceTable}
                            onChange={this.onChangePriceTable.bind(this)}
                        >
                            {priceTables.map((priceTable) => {
                                return <option key={priceTable} value={priceTable}>{priceTable}</option>
                            })}
                        </Select>

                        <div style={{display: 'flex', columnGap: formSpacing.columnGap, height: 40}}>
                            <Button
                                variant='outline'
                                icon='mdi mdi-plus'
                                label='Nieuw'
                                onClick={this.onClickOpenPriceTableModal.bind(this, '', '', {})}
                            />

                            <Button
                                variant='outline'
                                icon='mdi mdi-content-copy'
                                label='Kopiëren'
                                onClick={this.onClickOpenPriceTableModal.bind(this, `${selectedPriceTable} kopie`, '', priceTable)}
                            />

                            <Button
                                variant='outline'
                                icon='mdi mdi-pencil'
                                label='Bewerken'
                                onClick={this.onClickOpenPriceTableModal.bind(this, selectedPriceTable, selectedPriceTable, priceTable)}
                            />

                            {selectedPriceTable !== 'Standaard' &&
                                <Button
                                    variant='outline'
                                    icon='mdi mdi-delete'
                                    label='Verwijderen'
                                    onClick={() => {
                                        this.popup.open('Tarieftabel verwijderen', 'Weet je zeker dat je deze tarieftabel wilt verwijderen?', () => {
                                            this.onRemovePriceTable(selectedPriceTable)
                                            this.popup.close()
                                        })
                                    }}
                                />
                            }

                            <Button
                                variant='outline'
                                icon='mdi mdi-file-export'
                                label='Alle exporteren'
                                onClick={this.onClickExportTable.bind(this)}
                            />

                            <Button
                                variant='outline'
                                icon='mdi mdi-file-import'
                                label='Importeren'
                                onClick={this.onClickImportTable.bind(this)}
                            />
                        </div>
                    </div>

                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <div style={{display: 'flex', width: 330, height: 40}}>
                            <IconButton
                                disabled={!selectedCarrier.name}
                                tooltip='Kopieer vervoerder tarieven'
                                onClick={this.onClickCopyCarrierPriceTable.bind(this, priceTable, selectedCarrier)}
                            >
                                <i className='mdi mdi-content-copy' />
                            </IconButton>
                        </div>
                        <CarrierSelector
                            size={45}
                            carriers={enabledCarriers}
                            value={selectedCarrier.name}
                            onChange={this.onChangeCarrier.bind(this)}
                        />
                    </div>
                </div>

                {selectedCarrierProducts.map((productName) => {
                    if (productName == 'Standaard zending' || (reseller.settings.parcels.products[productName] && reseller.settings.parcels.products[productName][selectedCarrier.name])) {
                        let product = selectedCarrier.products[productName]

                        if (productName == 'Standaard zending') {
                            product = {type: 'product', countries: selectedCarrier.countries}
                        }

                        if (!priceTable?.[selectedCarrier.name]?.[productName]) {
                            return
                        }

                        return (
                            <div key={productName} style={{marginBottom: 12}}>
                                <S1 style={{marginBottom: 12}}>{productName}</S1>

                                {priceTable[selectedCarrier.name] && priceTable[selectedCarrier.name][productName] && priceTable[selectedCarrier.name][productName].countryGroups.map((priceRule, index) => {
                                    const priceScaleEnabled = priceTable[selectedCarrier.name]?.usePriceScales && priceTable[selectedCarrier.name][productName]?.usePriceScales

                                    return (
                                        <div key={`${selectedCarrier.name}${productName}${index}`} style={{display: 'flex', marginLeft: 15, marginBottom: 12}}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexShrink: 0,
                                                width: 285,
                                                height: 30
                                            }}
                                            >
                                                <S2>{priceRule.name}</S2>
                                            </div>

                                            <Select
                                                style={{width: 150, marginRight: 12}}
                                                value={priceRule.type}
                                                readOnly
                                            >
                                                {product.type === 'product' &&
                                                    <option value='fixed'>Vaste prijs</option>
                                                }

                                                {product.type === 'product' &&
                                                    <option value='weight'>Categorieën</option>
                                                }

                                                {product.type === 'product' &&
                                                    <option value='fixedKg'>Kilo toeslag</option>
                                                }

                                                {product.type !== 'product' &&
                                                    <option value='fee'>Toeslag</option>
                                                }

                                                {product.type !== 'product' &&
                                                    <option value='feepercent'>Toeslag %</option>
                                                }
                                            </Select>

                                            {priceRule.type === 'weight' && priceRule.categories.map((category, categoryIndex) => {
                                                return (
                                                    <div
                                                        key={categoryIndex}
                                                        style={{
                                                            width: 150,
                                                            flexShrink: 0,
                                                            marginRight: 12
                                                        }}
                                                    >
                                                        {priceScaleEnabled ?
                                                            <Input
                                                                label=''
                                                                value={category.name || (category.maxWeight ? `t/m ${category.maxWeight} kg` : `Cat ${categoryIndex + 1}`)}
                                                                readOnly
                                                            /> :
                                                            <CurrencyInput
                                                                label={category.name || (category.maxWeight ? `t/m ${category.maxWeight} kg` : `Cat ${categoryIndex + 1}`)}
                                                                value={category.price}
                                                                readOnly
                                                            />
                                                        }
                                                        {priceScaleEnabled &&
                                                            <FormStack style={{marginTop: formSpacing.rowGap}}>
                                                                {category.priceScales?.map((priceScale, i) => (
                                                                    <CurrencyInput
                                                                        key={i}
                                                                        label={`${category.priceScales[i - 1]?.maxAmount + 1 || 0}${priceScale.maxAmount ? ` - ${priceScale.maxAmount} st` : '+ stuks'}`}
                                                                        value={priceScale.price}
                                                                        readOnly
                                                                    />
                                                                ))}
                                                            </FormStack>
                                                        }
                                                    </div>
                                                )
                                            })}

                                            {(priceRule.type === 'fixed' || priceRule.type === 'fixedKg' || priceRule.type === 'fee' || priceRule.type === 'feepercent') &&
                                                <div style={{display: 'flex', width: 800, flexWrap: 'wrap', rowGap: formSpacing.rowGap}}>
                                                    {product.values ?
                                                        product.values.map((value) => {
                                                            return (
                                                                <div
                                                                    key={value}
                                                                    style={{
                                                                        width: 150,
                                                                        marginRight: 12
                                                                    }}
                                                                >
                                                                    {priceRule.type !== 'feepercent' &&
                                                                        <CurrencyInput
                                                                            label={`${productName === 'Verzekerd' && 'Tot: '}${value}`}
                                                                            value={priceRule.values[value]}
                                                                            readOnly
                                                                        />
                                                                    }

                                                                    {priceRule.type === 'feepercent' &&
                                                                        <Input
                                                                            label={`${productName === 'Verzekerd' && 'Tot: '}${value}`}
                                                                            value={priceRule.values[value]}
                                                                            append='%'
                                                                            readOnly
                                                                        />
                                                                    }
                                                                </div>
                                                            )
                                                        }) :
                                                        <div style={{width: 150, marginRight: 12}}>
                                                            {priceRule.type !== 'feepercent' &&
                                                                <>
                                                                    {priceScaleEnabled ?
                                                                        <FormStack>
                                                                            {priceRule.priceScales?.map((priceScale, i) => (
                                                                                <CurrencyInput
                                                                                    key={i}
                                                                                    label={`${priceRule.priceScales[i - 1]?.maxAmount + 1 || 0}${priceScale?.maxAmount ? ` - ${priceScale?.maxAmount} st` : '+ stuks'}`}
                                                                                    value={priceScale?.price}
                                                                                    readOnly
                                                                                />
                                                                            ))}
                                                                        </FormStack> :
                                                                        <CurrencyInput
                                                                            label='Prijs'
                                                                            value={priceRule.price}
                                                                            readOnly
                                                                        />
                                                                    }
                                                                </>
                                                            }
                                                            {priceRule.type === 'feepercent' &&
                                                                <Input
                                                                    label='Percentage'
                                                                    value={priceRule.price}
                                                                    append='%'
                                                                    readOnly
                                                                />
                                                            }

                                                        </div>
                                                    }

                                                    {priceRule.type === 'fixedKg' &&
                                                        <CurrencyInput
                                                            style={{width: 150, marginRight: 12}}
                                                            label='Toeslag per kg'
                                                            value={priceRule.fee}
                                                            readOnly
                                                        />
                                                    }

                                                    {priceRule.type === 'fixedKg' &&
                                                        <Select
                                                            style={{width: 150, marginRight: 12}}
                                                            label='Afronden op'
                                                            options={[
                                                                {title: 'kg', value: ''},
                                                                {title: '100g', value: '100g'},
                                                                {title: 'g', value: 'g'}
                                                            ]}
                                                            value={priceRule.round}
                                                            readOnly
                                                        />
                                                    }
                                                </div>
                                            }
                                        </div>
                                    )
                                })
                                }
                            </div>
                        )
                    }
                })}

                <H5 style={{marginBottom: 24, marginTop: 36}}>Toeslagen</H5>

                {reseller.settings.parcels.fees?.length > 0 &&
                    <Panel style={{width: 500, maxHeight: 500, overflowY: 'auto', marginBottom: 24}}>
                        {(reseller.settings.parcels.fees || []).map((fee, index) => {
                            return (
                                <ListItem
                                    key={`fee${index}`}
                                    onClick={() => this.parcelFeeModal.open(fee, index)}
                                >
                                    <P style={{flex: 1}}>{fee.description}</P>
                                    <P style={{marginLeft: 36}}>{`${fee.basedOn && fee.basedOn !== 'fixedPrice' ? '' : '€ '}${fee.price || '0,00'}${fee.basedOn && fee.basedOn !== 'fixedPrice' ? ' %' : ''}`}</P>
                                </ListItem>
                            )
                        })}
                    </Panel>
                }

                <Button
                    style={{marginTop: 24}}
                    variant='outline'
                    icon='mdi mdi-plus'
                    label='Nieuwe toeslag'
                    onClick={() => this.parcelFeeModal.open()}
                />

                <PriceTableModal
                    reseller={reseller}
                    carriers={carriers}
                    onChange={this.onEditPriceTable.bind(this)}
                    onAdd={this.onClickAddPriceTable.bind(this)}
                    onRemove={this.onRemovePriceTable.bind(this)}
                    onClickCopyCarrierPriceTable={this.onClickCopyCarrierPriceTable.bind(this)}
                    copiedPriceTable={copiedPriceTable}
                    ref={(ref) => this.PriceTableModal = ref}
                />

                <ImportPriceTableModal
                    reseller={reseller}
                    ref={(ref) => this.importPriceTableModal = ref}
                />

                <ParcelFeeModal
                    reseller={reseller}
                    carriers={carriers}
                    onRemove={this.onRemoveFee.bind(this)}
                    onChange={this.onSaveFee.bind(this)}
                    ref={(ref) => this.parcelFeeModal = ref}
                />

                <Popup
                    ref={(ref) => this.popup = ref}
                    cancelLabel='Terug'
                    confirmLabel='Verwijderen'
                />
            </>
        )
    }
}
